/**
 * 内置兜底文案（`T-732`）—— locale 文档**一份都拉不到**时的最后一道可读面。
 *
 * ## 为什么需要它（`T-732` 的取证结论）
 *
 * `T-727`／`T-729` 两份**真浏览器 E2E** 都撞到同一个形态：
 *
 * ```text
 * Error loading locale zh: AbortError: 请求超时：1000ms 内未完成（/locales/zh.json）
 * ```
 *
 * 旧实现在 locale 文档超时后**只**打印日志、不回填任何消息 ⇒ `vue-i18n` 的消息表为空 ⇒
 * `t()` 逐字返回**键名本身** ⇒ 界面上直接显示
 * `routes.device-simulator.top.connected`（`T-729` 现场读数：`.conn-text`）。
 * 这不是「测试假红」，是**真实用户**看到的破相（慢网首访即可复现）。
 *
 * ## 本文件的口径（三层可读面，永不裸显键名）
 *
 * | 层 | 触发 | 文案来源 |
 * |:--:|---|---|
 * | ① 正常 | locale JSON 命中 | `public/locales/<code>.json`（**唯一真相源**，未被本文件替代） |
 * | ② 兜底 | JSON 全部失败/超时 | **本文件**（默认语言 `zh` 的高频文案子集） |
 * | ③ 兜底外 | 键不在 ② 里 | `index.ts` 的 `missing` 处理器 ⇒ **末段人性化标签**（仍不是裸键名） |
 *
 * 🔴 **本文件是 `public/locales/zh.json` 的「经机械校验的子集」，不是第二真相源**：
 * `builtinFallback.spec.ts` 逐条断言「本文件的每个叶子键在 `zh.json` 里**存在且取值逐字相同**」
 * ⇒ 上游改文案而这里没跟 ⇒ **判红**（⛔ 不会静默漂移）。
 *
 * ## 为什么**不**把整份 `zh.json`（≈195 KiB / 3501 条）打进包
 *
 * 那等于把「运行期按语言加载」的设计目的（按语言取、不随主包走）整个推翻：主 chunk 会多出
 * ≈195 KiB 原始字节（gzip ≈45 KiB）**每次首访都要下载**，而兜底面只在「网络已经坏了」时才用得上。
 * ⇒ 本文件只收**首屏/启动可见面**的高频文案（`common.*` ＋ 设备模拟器连接状态条），
 * 其余键走 ③（人性化标签），代价是"文案不完美"，收益是"主包不膨胀、永不裸键"。
 */
export const BUILTIN_FALLBACK_LOCALE = 'zh'

/**
 * 默认语言（`zh`）的内置兜底文案。
 *
 * ⚠️ 新增/修改条目时**必须**与 `public/locales/zh.json` 逐字一致（`builtinFallback.spec.ts` 会判）。
 * ⚠️ 这里**只放无插值的叶子**（含 `{id}` / `{count}` 的条目留给 ③ 人性化标签，
 *    避免兜底面在没有入参时把 `{id}` 原样漏给用户）。
 */
export const BUILTIN_FALLBACK_MESSAGES = {
  common: {
    appName: '智能床',
    login: '登录',
    logout: '退出登录',
    profile: '个人信息',
    language: '语言',
    theme: '主题',
    help: '帮助与支持',
    loading: '加载中...',
    confirm: '确认',
    cancel: '取消',
    save: '保存',
    delete: '删除',
    edit: '编辑',
    add: '新增',
    select: '选择',
    search: '搜索',
    reset: '重置',
    submit: '提交',
    close: '关闭',
    back: '返回',
    refresh: '刷新',
    retry: '重试',
    export: '导出',
    download: '下载',
    upload: '上传',
    view: '查看',
    detail: '详情',
    description: '描述',
    operation: '操作',
    actions: '操作',
    settings: '设置',
    status: '状态',
    enabled: '已启用',
    disabled: '已禁用',
    noData: '暂无数据',
    loadFailed: '加载失败，请重试',
    operationFailed: '操作失败，请重试',
    pleaseInput: '请输入',
    pleaseSelect: '请选择',
  },
  routes: {
    'device-simulator': {
      top: {
        brand: '🛏️ 智能床设备模拟器',
        connected: '已连接',
        connecting: '连接中',
        disconnected: '未连接',
        connect: '▶ 连接',
        disconnect: '⏹ 断开',
      },
    },
    'device-simulator-v2': {
      topbar: {
        conn: {
          connected: '已连接',
          connecting: '连接中',
          disconnected: '未连接',
        },
      },
    },
  },
} as const
